import { computed, ref, type ComputedRef, type Ref } from 'vue'

import {
  productCustomizationApi,
  type ProductCustomizationApi,
  type ProductIdMappingStatus,
} from '../api'
import { productFeedSource } from '../configs/feedSources'
import { useProductMappingPlatforms } from '../configs/platforms'
import type {
  FeedPreview,
  FeedSourceDescriptor,
  InventoryMapping,
  ProductPlatform,
} from '../models/product.types'

import { useAccount, useWorkspace } from '@/features/account'
import { SaveAttributeFlow } from '@/features/workspace-settings'

export interface InventoryRow {
  source: string
  column: string
}

export interface UseProductCustomization {
  platforms: ComputedRef<readonly FeedSourceDescriptor[]>
  inventory: Ref<InventoryMapping>
  inventoryRows: ComputedRef<InventoryRow[]>
  loading: Ref<boolean>
  pending: Ref<boolean>
  error: Ref<unknown | null>
  load: () => Promise<void>
  productId: (platform: ProductPlatform) => string | null
  productIdMapping: (platform: ProductPlatform) => ProductIdMappingStatus | null
  productIdOptions: (source: FeedSourceDescriptor) => Promise<string[]>
  inventoryColumns: (source: FeedSourceDescriptor) => Promise<string[]>
  preview: (source: FeedSourceDescriptor) => Promise<FeedPreview>
  saveProductId: (source: FeedSourceDescriptor, field: string | null) => Promise<boolean>
  saveInventory: (source: FeedSourceDescriptor, column: string | null) => Promise<boolean>
}

export function useProductCustomization(
  api: ProductCustomizationApi = productCustomizationApi,
  attributes = new SaveAttributeFlow(),
): UseProductCustomization {
  const account = useAccount()
  const workspace = useWorkspace()
  const activePlatforms = useProductMappingPlatforms()
  const inventory = ref<InventoryMapping>({})
  const productMappings = ref<Record<string, ProductIdMappingStatus>>({})
  const loading = ref(false)
  const pending = ref(false)
  const error = ref<unknown | null>(null)

  const platforms = computed(() =>
    activePlatforms.value.map((platform) => productFeedSource(platform.field as ProductPlatform)),
  )
  const inventoryRows = computed(() =>
    Object.entries(inventory.value)
      .filter((entry): entry is [string, string] => Boolean(entry[1]))
      .map(([source, column]) => ({ source, column })),
  )

  function scope(): { accountId: number; workspaceId: number | string } {
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (accountId === undefined || workspaceId === undefined) {
      throw new Error('Product customization requires an account and workspace')
    }
    return { accountId, workspaceId }
  }

  async function load(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const { accountId, workspaceId } = scope()
      inventory.value = await api.getInventoryMapping(accountId, workspaceId)
      productMappings.value = await api.getProductIdMapping(accountId, workspaceId)
    } catch (cause) {
      inventory.value = {}
      productMappings.value = {}
      error.value = cause
    } finally {
      loading.value = false
    }
  }

  function productId(platform: ProductPlatform): string | null {
    const value = workspace.getAttribute(productFeedSource(platform).attributeKey)?.value
    const field = Array.isArray(value) ? value[0] : null
    return typeof field === 'string' ? field.replace(/^gpc_productattributes_/u, '') : null
  }

  function productIdMapping(platform: ProductPlatform): ProductIdMappingStatus | null {
    return productMappings.value[platform] ?? null
  }

  async function preview(source: FeedSourceDescriptor): Promise<FeedPreview> {
    const { workspaceId } = scope()
    return api.previewFeed({
      workspace_id: workspaceId,
      raw_data_type: source.rawDataType,
      ...(source.mergeWith ? { merge_with: source.mergeWith } : {}),
    })
  }

  async function productIdOptions(source: FeedSourceDescriptor): Promise<string[]> {
    const { accountId, workspaceId } = scope()
    return api.getProductIdOptions(accountId, workspaceId, source.platform)
  }

  async function inventoryColumns(source: FeedSourceDescriptor): Promise<string[]> {
    const { accountId, workspaceId } = scope()
    return api.getInventoryColumns(accountId, workspaceId, source.inventoryFeedSource)
  }

  async function saveProductId(
    source: FeedSourceDescriptor,
    field: string | null,
  ): Promise<boolean> {
    pending.value = true
    error.value = null
    try {
      const row = workspace.getAttribute(source.attributeKey)
      const result = await attributes.save(source.attributeKey, field ? [field] : null, {
        attributeId: typeof row?.id === 'number' ? row.id : null,
      })
      return result.written || result.verb === 'noop'
    } catch (cause) {
      error.value = cause
      return false
    } finally {
      pending.value = false
    }
  }

  async function saveInventory(
    source: FeedSourceDescriptor,
    column: string | null,
  ): Promise<boolean> {
    pending.value = true
    error.value = null
    try {
      const { accountId, workspaceId } = scope()
      await api.saveInventoryColumn(
        accountId,
        workspaceId,
        source.inventoryFeedSource,
        column ? [column] : [],
      )
      await load()
      return true
    } catch (cause) {
      error.value = cause
      return false
    } finally {
      pending.value = false
    }
  }

  return {
    platforms,
    inventory,
    inventoryRows,
    loading,
    pending,
    error,
    load,
    productId,
    productIdMapping,
    productIdOptions,
    inventoryColumns,
    preview,
    saveProductId,
    saveInventory,
  }
}
